/**
 * Bandeau de consentement cookies — conforme RGPD / recommandations CNIL.
 *
 * Règles appliquées :
 * - Aucun script/cookie non essentiel avant choix explicite de l'utilisateur.
 * - 3 boutons de même poids : Tout accepter / Tout refuser / Personnaliser.
 * - Refuser est aussi simple qu'accepter (un clic).
 * - Aucune case précochée pour cookies non essentiels.
 * - Choix mémorisé 6 mois (localStorage + expiration).
 * - Lien "Gérer mes cookies" en pied de page pour rouvrir à tout moment.
 * - Bloque PostHog (ap.emergent.sh) avant consentement.
 */
const CONSENT_KEY = "pv_cookie_consent_v1";
const CONSENT_TTL_DAYS = 180; // 6 mois

export function getConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.expires && Date.now() > parsed.expires) {
      localStorage.removeItem(CONSENT_KEY);
      return null;
    }
    return parsed;
  } catch { return null; }
}

export function saveConsent(prefs) {
  const payload = {
    ...prefs,
    saved_at: Date.now(),
    expires: Date.now() + CONSENT_TTL_DAYS * 24 * 3600 * 1000,
  };
  try { localStorage.setItem(CONSENT_KEY, JSON.stringify(payload)); } catch {}
  return payload;
}

/**
 * Blocks PostHog & any Emergent analytics endpoint at the network layer
 * BEFORE React mounts. Injected inline in <head> so it runs first.
 */
export function installNetworkBlocker() {
  if (window.__pvBlockerInstalled) return;
  window.__pvBlockerInstalled = true;
  const isBlocked = () => {
    const c = getConsent();
    // Analytics allowed only when explicitly accepted
    return !(c && c.analytics === true);
  };
  const shouldBlockUrl = (url) => {
    if (!url) return false;
    const u = String(url).toLowerCase();
    return (
      u.includes("ap.emergent.sh") ||
      u.includes("assets.emergent.sh") ||
      u.includes("posthog.com") ||
      u.includes("i.posthog.com") ||
      u.includes("us.posthog.com") ||
      u.includes("eu.posthog.com")
    );
  };

  // fetch
  const origFetch = window.fetch;
  window.fetch = function (input, init) {
    try {
      const url = typeof input === "string" ? input : input?.url;
      if (isBlocked() && shouldBlockUrl(url)) {
        return Promise.resolve(new Response("", { status: 204 }));
      }
    } catch {}
    return origFetch.apply(this, arguments);
  };

  // XHR
  const origOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (isBlocked() && shouldBlockUrl(url)) {
      this.__pv_blocked = true;
    }
    return origOpen.apply(this, arguments);
  };
  const origSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.send = function () {
    if (this.__pv_blocked) return;
    return origSend.apply(this, arguments);
  };

  // sendBeacon (PostHog uses it)
  if (navigator.sendBeacon) {
    const origBeacon = navigator.sendBeacon.bind(navigator);
    navigator.sendBeacon = function (url, data) {
      if (isBlocked() && shouldBlockUrl(url)) return true;
      return origBeacon(url, data);
    };
  }
}
